import { useQuery } from '@tanstack/react-query';
import {
  AnalyticFilterColumnDto,
  AnalyticSearchRequestDto,
  InvoiceAnalyticSearchRequestDto,
  InvoiceChargeSearchRequestDto,
  ShipmentSearchRequestDto,
} from 'src/generated/services/TFinancialApi';
import { tFinancialService } from 'src/infra/services/tFinancialService';
import { cacheKeysUtil } from 'src/utils/cacheKeysUtil';

export const useGetShipmentsData = (shipmentRequest: ShipmentSearchRequestDto, enabled = true) =>
  useQuery({
    queryKey: cacheKeysUtil.getShipmentsData(shipmentRequest),
    queryFn: async () => (await tFinancialService.api.fetchShipmentData(shipmentRequest)).data.data,
    enabled, // Only fetch when enabled is true
    refetchOnWindowFocus: false, // Prevent refetch when window regains focus
    staleTime: 30000, // Consider data fresh for 30 seconds to prevent unnecessary refetches
  });

export const useGetInvoicesData = (invoiceRequest: InvoiceAnalyticSearchRequestDto, enabled = true) =>
  useQuery({
    queryKey: cacheKeysUtil.getInvoicesData(invoiceRequest),
    queryFn: async () => (await tFinancialService.api.fetchInvoiceColumns(invoiceRequest)).data.data,
    enabled, // Only fetch when enabled is true
    refetchOnWindowFocus: false, // Prevent refetch when window regains focus
    staleTime: 30000, // Consider data fresh for 30 seconds to prevent unnecessary refetches
  });

export const useGetInvoiceChargesData = (invoiceChargeRequest: InvoiceChargeSearchRequestDto, enabled = true) =>
  useQuery({
    queryKey: cacheKeysUtil.getInvoiceChargesData(invoiceChargeRequest),
    queryFn: async () => (await tFinancialService.api.fetchInvoiceChargeColumns(invoiceChargeRequest)).data.data,
    enabled, // Only fetch when enabled is true
    refetchOnWindowFocus: false, // Prevent refetch when window regains focus
    staleTime: 30000, // Consider data fresh for 30 seconds to prevent unnecessary refetches
  });

export const useGetAnalyticsFilterColumnData = (
  table: 'SHIPMENTS' | 'INVOICES' | 'CHARGES',
  columnName: string,
  searchRequest: AnalyticSearchRequestDto,
  searchValue = '',
  page?: { pageNumber: number; pageSize: number },
  enabled = true
) =>
  useQuery<AnalyticFilterColumnDto | undefined>({
    queryKey: cacheKeysUtil.getAnalyticsFilterColumnData(table, columnName, searchRequest, searchValue, page),
    queryFn: async () =>
      (
        await tFinancialService.api.getFilterColumnData(table, columnName, {
          searchRequest,
          searchValue,
          page,
        })
      ).data.data,
    enabled: enabled && !!searchRequest?.currencyReference && !!columnName,
    refetchOnWindowFocus: false,
    staleTime: 0.5 * 60 * 1000,
    cacheTime: 60 * 1000,
  });
